import { useCallback, useEffect, useState } from 'react';
import {
  fetchProjectOversightSummaries,
  type ProjectOversightClientSummary,
  type ProjectOversightReportItem,
} from '../services/projectOversightApi';
import type { ProjectOversightMode } from '../pages/sa/projectOversight/projectOversightConfig';

export type ProjectOversightRecord = {
  id: string;
  title: string;
  subtitle?: string;
  clientName?: string;
  clientUserId?: number;
  smeName?: string;
  status?: string;
  lastActivity?: string;
  generatedAt?: string;
  submittedAt?: string;
  fileName?: string;
  reportItemId?: string;
  projectId?: number;
};

const extractFileName = (filePath?: string | null) => {
  if (!filePath) return '';
  const normalized = String(filePath).replace(/\\/g, '/');
  const parts = normalized.split('/');
  return parts[parts.length - 1] || '';
};

const mapClientSummary = (row: ProjectOversightClientSummary): ProjectOversightRecord => ({
  id: String(row.clientUserId),
  title: row.clientName || `Client ${row.clientUserId}`,
  clientName: row.clientName,
  clientUserId: row.clientUserId,
  smeName: row.assignedSmeName || undefined,
  status: row.status,
  lastActivity: row.lastActivityUtc || undefined,
  subtitle: row.designation || undefined,
});

const mapReport = (row: ProjectOversightReportItem, projectId: number): ProjectOversightRecord => {
  const reportItemId = row.reportItemId?.trim() || '';
  const remarks = row.remarks?.trim() || '';
  return {
    id: reportItemId || String(row.id ?? ''),
    title: reportItemId || remarks.slice(0, 60) || 'Project report',
    subtitle: remarks || undefined,
    generatedAt: row.reportDate || undefined,
    submittedAt: row.submittedAt || undefined,
    fileName: extractFileName(row.attachmentUrl) || undefined,
    reportItemId: reportItemId || undefined,
    projectId,
    status: row.status?.trim() || (row.attachmentUrl?.trim() ? 'submitted' : 'upload'),
  };
};

type UseProjectOversightRecordsResult = {
  records: ProjectOversightRecord[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
};

export function useProjectOversightRecords(
  mode: ProjectOversightMode,
  projectId: number | null
): UseProjectOversightRecordsResult {
  const [records, setRecords] = useState<ProjectOversightRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectId) {
      setRecords([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchProjectOversightSummaries(mode, projectId);

      if (mode === 'report') {
        setRecords((data as ProjectOversightReportItem[]).map((row) => mapReport(row, projectId)));
      } else {
        setRecords((data as ProjectOversightClientSummary[]).map(mapClientSummary));
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load records for this project.';
      setError(message);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [mode, projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { records, loading, error, reload };
}
